// Angular
import { Injectable } from "@angular/core";

// RX JS
import { BehaviorSubject, catchError, Observable, of } from "rxjs";

// Models
import { Permission } from "#models/permissions";
import { User, UserOrNull } from "#models/user";

// Services
import { BaseRequestService } from "#services/base-request/base-request.service";

@Injectable({
    providedIn: "root",
})
export class UserService extends BaseRequestService<User> {
    private userSubject = new BehaviorSubject<UserOrNull>(null);

    override endpoint = `user-profile`;

    get user$(): Observable<UserOrNull> {
        return this.userSubject.asObservable();
    }

    resetPassword(data: {
        password: string;
        password_confirmation: string;
    }): Observable<{ message?: string }> {
        return this.http.put<{ message?: string }>(`${this.baseApiUrl}/password`, data).pipe(
            catchError((err) => {
                this.handleRequestError(err);
                return of(err);
            })
        );
    }

    setUser(user: UserOrNull) {
        this.userSubject.next(user);
    }

    updateUser(data: Partial<UserOrNull>) {
        const currentUser = this.getUser() ?? {};
        const updatedUser = { ...currentUser, ...data } as UserOrNull;
        this.userSubject.next(updatedUser);
    }

    getUser(): UserOrNull {
        return this.userSubject.value;
    }

    clearUser(): void {
        this.userSubject.next(null);
    }

    getRole(): User["role"] | null {
        return this.getUser()?.role || null;
    }

    getPermissions(): Permission[] {
        return this.getUser()?.permissions || [];
    }

    can(permission: Permission): boolean {
        const permissions = this.getPermissions();

        if (permissions.length) {
            return permissions.includes(permission);
        }

        return false;
    }
}
